import React, { useState } from 'react';
import { X, ExternalLink, Sparkles, Crown, ShieldCheck } from 'lucide-react';
import { ALL_AD_SLOTS, MOCK_ADS, AdPlacement } from './adData';
import { useUser } from '../../contexts/UserContext';
interface AdSlotProps {
  slotId: string;
  className?: string;
  onNavigate?: (page: string) => void;
  showDebug?: boolean;
}
/** Check if a Tailwind bg/gradient class is visually dark */
function isDark(bg?: string): boolean {
  if (!bg) return false;
  // Match shades 500–950 for any color
  if (/-(5\d{2}|6\d{2}|7\d{2}|8\d{2}|9\d{2}|950)\b/.test(bg)) return true;
  // Explicit dark patterns
  if (/slate-[6-9]|gray-[6-9]|zinc-[6-9]|neutral-[6-9]|stone-[6-9]/.test(bg)) return true;
  return false;
}
export function AdSlot({
  slotId,
  className = '',
  onNavigate,
  showDebug = false
}: AdSlotProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const {
    user
  } = useUser();
  const isAdmin = user?.role === 'admin';
  const slotConfig = ALL_AD_SLOTS.find(s => s.id === slotId);
  const ads = MOCK_ADS[slotId] || [];
  const visibleAds = ads.filter(ad => !dismissed.has(ad.id));
  if (!slotConfig || slotConfig.status !== 'active' || visibleAds.length === 0) {
    return null;
  }
  const handleDismiss = (adId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissed(prev => new Set(prev).add(adId));
  };
  const handleClick = (ad: AdPlacement) => {
    if (onNavigate) onNavigate('advertising');
  };
  const debug = showDebug || isAdmin;
  const label = debug ? `${slotId} · Реклама` : 'Реклама';
  // ─── Shared: top-right label + close ───
  const TopControls = ({
    adId,
    dark = false
  }: {
    adId: string;
    dark?: boolean;
  }) => <div className="absolute top-2 right-2 z-10 flex items-center gap-1" data-id="element-12725">
      <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${dark ? 'bg-black/25 text-white/70 border border-white/15' : 'bg-white/85 backdrop-blur-sm text-slate-400 border border-slate-200/80 shadow-sm'}`} data-id="element-12726">
        {label}
      </span>
      <button onClick={e => handleDismiss(adId, e)} className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors ${dark ? 'bg-black/25 text-white/70 hover:bg-black/40' : 'bg-white/85 backdrop-blur-sm text-slate-400 hover:text-slate-600 border border-slate-200/80 shadow-sm'}`} data-id="element-12727">
        <X className="w-2.5 h-2.5" data-id="element-12728" />
      </button>
    </div>;
  // ════════════════════════════════════════
  // 1. STICKY — mobile bottom bar
  // ════════════════════════════════════════
  if (slotConfig.type === 'Sticky') {
    const ad = visibleAds[0];
    return <div className={`fixed bottom-0 left-0 right-0 z-50 sm:hidden ${ad.bgColor || 'bg-indigo-600'} text-white px-3 py-2.5 shadow-[0_-4px_20px_rgba(0,0,0,0.15)] ${className}`} data-id="element-12729">
        <div className="flex items-center justify-between gap-2" data-id="element-12730">
          <div className="flex-1 min-w-0" data-id="element-12731">
            <div className="text-[8px] font-bold uppercase tracking-wider text-white/50 mb-0.5" data-id="element-12732">
              {label}
            </div>
            <div className="text-xs font-bold leading-tight truncate" data-id="element-12733">
              {ad.title}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0" data-id="element-12734">
            <button onClick={() => handleClick(ad)} className="px-3 py-1.5 bg-white text-indigo-900 text-[11px] font-bold rounded-lg whitespace-nowrap" data-id="element-12735">
              {ad.ctaText}
            </button>
            <button onClick={e => handleDismiss(ad.id, e)} className="p-1 hover:bg-white/20 rounded-full transition-colors" data-id="element-12736">
              <X className="w-3.5 h-3.5" data-id="element-12737" />
            </button>
          </div>
        </div>
      </div>;
  }
  // ════════════════════════════════════════
  // 2. PINNED / CARD — inline native cards
  // ════════════════════════════════════════
  if (slotConfig.type === 'Pinned' || slotConfig.size === 'Карточка') {
    return <>
        {visibleAds.map(ad => <div key={ad.id} onClick={() => handleClick(ad)} className={`relative rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-white p-3.5 sm:p-4 cursor-pointer hover:border-indigo-200 hover:shadow-sm transition-all group ${className}`} data-id="element-12738">
            <TopControls adId={ad.id} data-id="element-12739" />

            <div className="flex items-start gap-3 pr-20" data-id="element-12740">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${ad.bgColor || 'bg-indigo-100 text-indigo-600'}`} data-id="element-12741">
                {ad.imageUrl ? <img src={ad.imageUrl} alt="" className="w-full h-full object-cover rounded-lg" data-id="element-12742" /> : <Sparkles className="w-4 h-4" data-id="element-12743" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-12744">
                <div className="flex items-center gap-1.5 mb-0.5 flex-wrap" data-id="element-12745">
                  <span className="font-semibold text-[13px] text-slate-800 group-hover:text-indigo-600 transition-colors leading-tight" data-id="element-12746">
                    {ad.title}
                  </span>
                  {ad.badge === 'TOP' && <span className="px-1 py-px rounded bg-amber-100 text-amber-700 text-[8px] font-bold inline-flex items-center gap-0.5" data-id="element-12747">
                      <Crown className="w-2 h-2" data-id="element-12748" /> TOP
                    </span>}
                  {ad.badge === 'PRO' && <span className="px-1 py-px rounded bg-indigo-100 text-indigo-700 text-[8px] font-bold inline-flex items-center gap-0.5" data-id="element-12749">
                      <ShieldCheck className="w-2 h-2" data-id="element-12750" /> PRO
                    </span>}
                </div>
                {ad.description && <p className="text-[11px] text-slate-500 mb-1.5 line-clamp-2 leading-relaxed" data-id="element-12751">
                    {ad.description}
                  </p>}
                <span className="text-[11px] font-semibold text-indigo-600 inline-flex items-center gap-1" data-id="element-12752">
                  {ad.ctaText} <ExternalLink className="w-2.5 h-2.5" data-id="element-12753" />
                </span>
              </div>
            </div>
          </div>)}
      </>;
  }
  // ════════════════════════════════════════
  // 3. SIDEBAR — 300×600 / 300×250
  // ════════════════════════════════════════
  if (slotConfig.size === '300×600' || slotConfig.size === '300×250') {
    const ad = visibleAds[0];
    const dark = isDark(ad.bgColor);
    const isCompact = slotConfig.size === '300×250';
    return <div className={`relative rounded-xl overflow-hidden border border-slate-200 bg-white ${className}`} data-id="element-12754">
        {/* Visual header area */}
        <div className={`relative w-full ${isCompact ? 'h-28' : 'h-40'} ${ad.bgColor || 'bg-gradient-to-br from-indigo-500 to-purple-600'} flex items-end p-3`} data-id="element-12755">
          <TopControls adId={ad.id} dark={isDark(ad.bgColor) || !ad.bgColor} data-id="element-12756" />
          {ad.imageUrl ? <img src={ad.imageUrl} alt={ad.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-12757" /> : <span className="text-white/10 text-4xl font-black absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 select-none" data-id="element-12758">
              AD
            </span>}
        </div>

        {/* Content */}
        <div className="p-3.5" data-id="element-12759">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-12760">
            {ad.advertiser}
          </p>
          <h4 className="font-bold text-slate-900 text-[13px] mb-1 leading-snug line-clamp-2" data-id="element-12761">
            {ad.title}
          </h4>
          {ad.description && <p className="text-[11px] text-slate-500 mb-3 leading-relaxed line-clamp-3" data-id="element-12762">
              {ad.description}
            </p>}
          <button onClick={() => handleClick(ad)} className={`w-full py-2 px-3 rounded-lg text-[12px] font-semibold text-white transition-all hover:opacity-90 ${ad.accentColor || 'bg-indigo-600'}`} data-id="element-12763">
            {ad.ctaText}
          </button>
        </div>
      </div>;
  }
  // ════════════════════════════════════════
  // 4. DEFAULT — Horizontal banners (728×90, 1140×250, etc.)
  // ════════════════════════════════════════
  const ad = visibleAds[0];
  const dark = isDark(ad.bgColor);
  return <div className={`relative rounded-xl overflow-hidden ${dark ? 'border border-white/5' : 'border border-slate-200'} ${ad.bgColor || 'bg-white'} ${className}`} data-id="element-12764">
      <TopControls adId={ad.id} dark={dark} data-id="element-12765" />

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center p-4 sm:px-6 sm:py-5 gap-3 sm:gap-5" data-id="element-12766">
        {/* Text block */}
        <div className="flex-1 min-w-0 pt-4 sm:pt-0" data-id="element-12767">
          <p className={`text-[9px] font-bold uppercase tracking-wider mb-0.5 ${dark ? 'text-white/50' : 'text-slate-400'}`} data-id="element-12768">
            {ad.advertiser}
          </p>
          <h3 className={`font-bold text-sm sm:text-base mb-1 leading-snug ${dark ? 'text-white' : 'text-slate-900'}`} data-id="element-12769">
            {ad.title}
          </h3>
          {ad.description && <p className={`text-[11px] sm:text-xs leading-relaxed line-clamp-2 ${dark ? 'text-white/65' : 'text-slate-500'}`} data-id="element-12770">
              {ad.description}
            </p>}
        </div>

        {/* CTA */}
        <div className="shrink-0" data-id="element-12771">
          <button onClick={() => handleClick(ad)} className={`w-full sm:w-auto inline-flex items-center justify-center gap-1.5 py-2 sm:py-2.5 px-4 sm:px-5 rounded-lg text-[12px] sm:text-[13px] font-bold transition-all hover:shadow-md ${dark ? 'bg-white text-slate-900 hover:bg-white/90' : `text-white ${ad.accentColor || 'bg-indigo-600'} hover:opacity-90`}`} data-id="element-12772">
            {ad.ctaText}
            <ExternalLink className="w-3 h-3" data-id="element-12773" />
          </button>
        </div>
      </div>
    </div>;
}